/**
 * Rate Limiting and Debouncing Utilities
 * 
 * Provides client-side rate limiting to prevent spam and reduce server load.
 * Includes debounce, throttle, and rate limiting functions.
 */

/**
 * Debounce function - delays execution until after wait time has elapsed since last call
 * Useful for search inputs, resize handlers, etc.
 * 
 * @param {Function} func - Function to debounce
 * @param {number} wait - Milliseconds to wait
 * @param {Object} options - Options { leading: boolean, trailing: boolean, maxWait: number }
 * @returns {Function} - Debounced function with cancel() and flush() methods
 * 
 * @example
 * const debouncedSearch = debounce((query) => searchAPI(query), 300);
 * debouncedSearch('test'); // Only calls API after 300ms of no more calls
 */
export const debounce = (func, wait = 300, options = {}) => {
  const { leading = false, trailing = true, maxWait } = options;
  
  let timeoutId = null;
  let lastCallTime = 0;
  let lastInvokeTime = 0;
  let lastArgs = null;
  let lastThis = null;
  let result = null;

  const invokeFunc = (time) => {
    const args = lastArgs;
    const thisArg = lastThis;
    
    lastArgs = lastThis = null;
    lastInvokeTime = time;
    result = func.apply(thisArg, args);
    return result;
  };

  const shouldInvoke = (time) => {
    const timeSinceLastCall = time - lastCallTime;
    const timeSinceLastInvoke = time - lastInvokeTime;
    
    return (
      lastCallTime === 0 ||
      timeSinceLastCall >= wait ||
      timeSinceLastCall < 0 ||
      (maxWait !== undefined && timeSinceLastInvoke >= maxWait)
    );
  };

  const trailingEdge = (time) => {
    timeoutId = null;
    
    if (trailing && lastArgs) {
      return invokeFunc(time);
    }
    lastArgs = lastThis = null;
    return result;
  };

  const timerExpired = () => {
    const time = Date.now();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    
    const timeSinceLastCall = time - lastCallTime;
    const timeSinceLastInvoke = time - lastInvokeTime;
    const timeWaiting = wait - timeSinceLastCall;
    const remainingWait = maxWait !== undefined
      ? Math.min(timeWaiting, maxWait - timeSinceLastInvoke)
      : timeWaiting;
    
    timeoutId = setTimeout(timerExpired, remainingWait);
  };

  const debounced = function(...args) {
    const time = Date.now();
    const isInvoking = shouldInvoke(time);
    
    lastArgs = args;
    lastThis = this;
    lastCallTime = time;
    
    if (isInvoking) {
      if (timeoutId === null) {
        lastInvokeTime = lastCallTime;
        if (leading) {
          return invokeFunc(lastCallTime);
        }
      }
      
      if (maxWait !== undefined) {
        timeoutId = setTimeout(timerExpired, wait);
        return invokeFunc(lastCallTime);
      }
    }
    
    if (timeoutId === null) {
      timeoutId = setTimeout(timerExpired, wait);
    }
    
    return result;
  };

  debounced.cancel = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
    lastInvokeTime = 0;
    lastArgs = lastCallTime = lastThis = timeoutId = null;
  };

  debounced.flush = () => {
    return timeoutId === null ? result : trailingEdge(Date.now());
  };

  debounced.pending = () => {
    return timeoutId !== null;
  };

  return debounced;
};

/**
 * Throttle function - limits execution to once per wait period
 * Useful for scroll handlers, resize handlers, etc.
 * 
 * @param {Function} func - Function to throttle
 * @param {number} wait - Milliseconds to wait between calls
 * @param {Object} options - Options { leading: boolean, trailing: boolean }
 * @returns {Function} - Throttled function
 * 
 * @example
 * const throttledScroll = throttle(() => handleScroll(), 100);
 * window.addEventListener('scroll', throttledScroll);
 */
export const throttle = (func, wait = 300, options = {}) => {
  const { leading = true, trailing = true } = options;
  
  return debounce(func, wait, {
    leading,
    trailing,
    maxWait: wait,
  });
};

/**
 * Rate Limiter class - prevents too many calls within a time window
 * Useful for API calls, form submissions, etc.
 * 
 * @example
 * const limiter = new RateLimiter(5, 60000); // 5 calls per minute
 * if (limiter.tryAcquire()) {
 *   await submitForm();
 * } else {
 *   showError('Too many requests');
 * }
 */
export class RateLimiter {
  constructor(maxCalls = 10, windowMs = 60000) {
    this.maxCalls = maxCalls;
    this.windowMs = windowMs;
    this.calls = [];
  }

  /**
   * Try to acquire permission for a call
   * @returns {boolean} - True if call is allowed, false if rate limited
   */
  tryAcquire() {
    const now = Date.now();
    
    // Remove calls outside the time window
    this.calls = this.calls.filter(callTime => now - callTime < this.windowMs);
    
    // Check if we're under the limit
    if (this.calls.length < this.maxCalls) {
      this.calls.push(now);
      return true;
    }
    
    return false;
  }

  /**
   * Get time until next call is allowed (in milliseconds)
   * @returns {number} - Milliseconds until next call allowed, or 0 if call allowed now
   */
  getTimeUntilNextCall() {
    if (this.calls.length < this.maxCalls) {
      return 0;
    }
    
    const oldestCall = Math.min(...this.calls);
    const timeUntilExpiry = this.windowMs - (Date.now() - oldestCall);
    return Math.max(0, timeUntilExpiry);
  }

  /**
   * Get number of remaining calls in current window
   * @returns {number} - Number of calls remaining
   */
  getRemainingCalls() {
    const now = Date.now();
    this.calls = this.calls.filter(callTime => now - callTime < this.windowMs);
    return Math.max(0, this.maxCalls - this.calls.length);
  }

  /**
   * Reset the rate limiter
   */
  reset() {
    this.calls = [];
  }
}

/**
 * Create a rate-limited version of a function
 * 
 * @param {Function} func - Function to rate limit
 * @param {number} maxCalls - Maximum calls per window
 * @param {number} windowMs - Time window in milliseconds
 * @param {Function} onRateLimited - Optional callback when rate limited
 * @returns {Function} - Rate-limited function
 * 
 * @example
 * const limitedSubmit = createRateLimitedFunction(
 *   submitForm,
 *   3,
 *   60000,
 *   () => alert('Too many submissions!')
 * );
 */
export const createRateLimitedFunction = (
  func,
  maxCalls = 10,
  windowMs = 60000,
  onRateLimited = null
) => {
  const limiter = new RateLimiter(maxCalls, windowMs);

  const rateLimitedFunc = async (...args) => {
    if (limiter.tryAcquire()) {
      return await func(...args);
    } else {
      if (onRateLimited) {
        const timeUntilNext = limiter.getTimeUntilNextCall();
        onRateLimited(timeUntilNext);
      }
      throw new Error(`Rate limit exceeded. Try again in ${Math.ceil(limiter.getTimeUntilNextCall() / 1000)} seconds.`);
    }
  };

  rateLimitedFunc.getRemainingCalls = () => limiter.getRemainingCalls();
  rateLimitedFunc.getTimeUntilNextCall = () => limiter.getTimeUntilNextCall();
  rateLimitedFunc.reset = () => limiter.reset();

  return rateLimitedFunc;
};

/**
 * Memoize a function with time-based cache expiration
 * Useful for expensive calculations or API calls
 * 
 * @param {Function} func - Function to memoize
 * @param {number} ttl - Time to live in milliseconds (default: 5 minutes)
 * @param {Function} keyGenerator - Optional function to generate cache key from arguments
 * @returns {Function} - Memoized function with cache.clear() method
 * 
 * @example
 * const memoizedFetch = memoize(fetchData, 300000); // Cache for 5 minutes
 */
export const memoize = (func, ttl = 300000, keyGenerator = JSON.stringify) => {
  const cache = new Map();

  const memoized = function(...args) {
    const key = keyGenerator(args);
    const cached = cache.get(key);
    
    if (cached && Date.now() - cached.timestamp < ttl) {
      return cached.value;
    }
    
    const result = func.apply(this, args);
    cache.set(key, { value: result, timestamp: Date.now() });
    
    return result;
  };

  memoized.cache = cache;
  memoized.clear = () => cache.clear();

  return memoized;
};

export default {
  debounce,
  throttle,
  RateLimiter,
  createRateLimitedFunction,
  memoize,
};

